'use client';

import React, { CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { getRegionalUrl } from '@/lib/regionalUrl';
import './FlareButton.css';
import tinycolor from 'tinycolor2';

interface ButtonProps {
  label: string;
  variant?:
    | 'black'
    | 'white'
    | 'outline'
    | 'auth'
    | 'ghost'
    | 'error'
    | 'remove'
    | 'main'
    | 'fill';
  className?: string;
  onClick?: () => void;
  color?: string;
  to?: string;
  disabled?: boolean;
  icon?: string;
  newTab?: boolean;
  animation?: 'none' | 'fill' | 'outline';
}

const Button: React.FC<ButtonProps> = ({
  label = 'Learn More',
  variant = 'fill',
  className = '',
  onClick,
  color = '#000',
  to,
  disabled,
  icon,
  newTab,
  animation = 'outline',
}) => {
  const params = useParams<{ countryCode?: string }>();
  const buttonStyle = {
    black: 'bg-black border-black text-white',
    white: 'bg-white! hover:bg-white border-white text-black',
    outline: 'bg-transparent border-black',
    auth: '!bg-white hover:!text-black hover:!bg-black/10 hover:!border-black/15',
    ghost: 'bg-transparent inherit text-inherit opacity-30',
    error: '!bg-transparent !text-red-500 !border-red-500 ',
    remove: '!hidden',
    main: 'bg-black border-black text-white hover:!bg-gray-dark hover:!border-gray-dark',
    fill: 'border-0 opacity-100', // No border for fill variant
  }[variant];

  const isLight: boolean = tinycolor(color).isLight();
  const withArrow: boolean = label?.trim().endsWith('>');

  const linkProps = newTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  if (!label) return null;

  const divStyle = color
    ? ({
        '--customColor': color,
      } as CSSProperties)
    : undefined;

  const buttonComponent = (
    <button
      disabled={disabled}
      style={divStyle}
      className={cn(
        'group py-3 px-6 w-max font-bold text-sm lg:text-base border-2 transition-all duration-300',
        // Conditional border radius - 12px for fill variant, default rounded-full for others
        variant === 'fill' ? 'rounded-xl' : 'rounded-full',
        buttonStyle,
        // Special styling for fill variant
        variant === 'fill' && color
          ? isLight
            ? `text-black bg-[var(--customColor)] hover:opacity-80`
            : `text-white bg-[var(--customColor)] hover:opacity-80`
          : variant === 'fill' && !color
            ? 'bg-black text-white hover:opacity-80'
            : // Original color logic for other variants
              color && isLight
              ? animation === 'outline'
                ? `text-[var(--customColor)] hover:text-black hover:bg-[var(--customColor)] border-[var(--customColor)]`
                : animation === 'fill'
                  ? `text-black hover:text-[var(--customColor)] bg-[var(--customColor)] hover:bg-transparent border-[var(--customColor)]`
                  : `text-black hover:text-black bg-[var(--customColor)]`
              : animation === 'outline'
                ? `text-[var(--customColor)] hover:text-white bg-transparent hover:bg-[var(--customColor)] border-[var(--customColor)]`
                : animation === 'fill'
                  ? `text-white hover:text-[var(--customColor)] bg-[var(--customColor)] hover:bg-transparent border-[var(--customColor)]`
                  : `text-white hover:text-white bg-[var(--customColor)]`,
        !color && variant !== 'fill' && 'text-black',
        className,
        disabled && 'opacity-30 cursor-not-allowed'
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-center gap-3 relative">
        {icon && (
          <Image
            className="  w-5 h-5 object-cover"
            src={icon}
            alt="icon"
            width={20}
            height={20}
          />
        )}
        {label.trim().replace(/\s*>\s*$/, '')}
        {withArrow && (
          <span className="group-hover:translate-x-2 transition-transform duration-300">
            {/* <svg
              xmlns="http://www.w3.org/2000/svg"
              width={18}
              height={18}
              viewBox="0 0 24 24"
            >
              <path
                d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                fill="currentColor"
              ></path>
            </svg> */}

            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 256 256"
            >
              <path
                d="M184.49,136.49l-80,80a12,12,0,0,1-17-17L159,128,87.51,56.49a12,12,0,1,1,17-17l80,80A12,12,0,0,1,184.49,136.49Z"
                fill="currentColor"
              ></path>
            </svg>
          </span>
        )}
      </div>
    </button>
  );

  const targetUrl = to ? getRegionalUrl(to, params?.countryCode) : undefined;
  const isExternalProtocol =
    targetUrl?.startsWith('tel:') ||
    targetUrl?.startsWith('mailto:') ||
    targetUrl?.startsWith('sms:');

  if (targetUrl) {
    if (isExternalProtocol) {
      return (
        <a
          {...linkProps}
          href={targetUrl}
          aria-label="..."
          className="no-underline flex items-center justify-center"
        >
          {buttonComponent}
          <span className="absolute w-[1px] h-[1px] text-transparent overflow-hidden">
            {label} Details
          </span>
        </a>
      );
    }

    return (
      <Link
        {...linkProps}
        href={targetUrl}
        prefetch={true}
        aria-label="..."
        className="no-underline flex items-center justify-center"
      >
        {buttonComponent}
        <span className="absolute w-[1px] h-[1px] text-transparent overflow-hidden">
          {label} Details
        </span>
      </Link>
    );
  }

  return buttonComponent;
};

export default Button;
